<script setup>
import { useScreenSize } from '~/composables/useScreenSize'

const props = defineProps({
  text: {
    type: String,
    default: 'Tooltip text',
  },
  position: {
    default: 'top',
    type: String,
  },
  variant: {
    type: String,
    default: 'default',
  },
  maxHeight: {
    type: String,
    default: 'auto',
  },
  forcePosition: {
    type: String,
    default: null,
  },
  teleportToBody: {
    type: Boolean,
    default: false,
  },
})

const finalPosition = ref(props.position)
const showTooltip = ref(false)
const tooltipElement = ref(null)

const { screenWidth, screenHeight } = useScreenSize()

function adjustTooltipPosition() {
  if (!tooltipElement.value || typeof window === 'undefined') return

  if (props.forcePosition) {
    finalPosition.value = props.forcePosition
    return
  }

  const rect = tooltipElement.value.getBoundingClientRect()
  const { value: viewportWidth } = screenWidth
  const { value: viewportHeight } = screenHeight
  const tooltipDimensions = { width: 280, height: 120 }
  const margin = 20

  if (viewportWidth < 800) {
    const mobilePositions = ['right', 'top', 'bottom']
    finalPosition.value = mobilePositions.includes(props.position) ? 'left' : props.position
    return
  }

  const collisionChecks = {
    top: () => rect.top < tooltipDimensions.height + margin,
    bottom: () => rect.bottom + tooltipDimensions.height + margin > viewportHeight,
    left: () => rect.left < tooltipDimensions.width + margin,
    right: () => rect.right + tooltipDimensions.width + margin > viewportWidth,
  }

  const fallbackPositions = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  }

  const hasCollision = collisionChecks[props.position]?.()
  finalPosition.value = hasCollision ? fallbackPositions[props.position] : props.position
}

function onMouseEnter() {
  showTooltip.value = true
  nextTick(() => {
    adjustTooltipPosition()
  })
}

function onMouseLeave() {
  showTooltip.value = false
}

function handleResize() {
  if (showTooltip.value) {
    adjustTooltipPosition()
  }
}

watch([screenWidth, screenHeight], handleResize)

onMounted(() => {
  if (props.variant === 'white') {
    nextTick(() => {
      adjustTooltipPosition()
    })
  }
})

function getTooltipPosition() {
  if (!tooltipElement.value) return {}

  const rect = tooltipElement.value.getBoundingClientRect()
  const offset = 10
  const tooltipHeight = 60

  const positionConfigs = {
    left: {
      right: `${window.innerWidth - rect.left + offset}px`,
      top: `${rect.top + rect.height / 2}px`,
      transform: 'translateY(-50%)',
    },
    right: {
      left: `${rect.right + offset}px`,
      top: `${rect.top + rect.height / 2}px`,
      transform: 'translateY(-50%)',
    },
    bottom: {
      left: `${rect.left + rect.width / 2}px`,
      top: `${rect.bottom + offset}px`,
      transform: 'translateX(-50%)',
    },
    top: {
      left: `${rect.left + rect.width / 2}px`,
      top: `${rect.top - tooltipHeight - (offset * 3)}px`,
      transform: 'translateX(-50%)',
    },
  }

  return {
    position: 'fixed',
    zIndex: 999999,
    ...positionConfigs[props.position] || positionConfigs.top,
  }
}
</script>

<template>
  <span
    ref="tooltipElement"
    :data-tooltip="text"
    :position="position"
    :data-variant="variant"
    :data-max-height="maxHeight"
    :data-final-position="finalPosition"
    :class="{ 'show-tooltip': showTooltip, 'tooltip-teleported': teleportToBody }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <slot />

    <Teleport v-if="teleportToBody && showTooltip" to="body">
      <div
        class="tooltip-content-teleported"
        :data-variant="variant"
        :data-final-position="position"
        :style="getTooltipPosition()"
      >
        {{ text }}
      </div>
    </Teleport>
  </span>
</template>

<style lang="scss" scoped>
$tooltip-bg-default: #505050;
$tooltip-bg-white: white;
$tooltip-border: #e5e7eb;
$tooltip-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
$tooltip-z-index: 99999;

[data-tooltip] {
  @apply relative;

  & > * {
    @apply inline-block;
  }

  &::before,
  &::after {
    @apply absolute hidden opacity-0 pointer-events-none;
    user-select: none;
    z-index: $tooltip-z-index !important;
  }

  &::before {
    @apply content-[''] border-transparent border-[5px];
  }

  &::after {
    @apply content-[attr(data-tooltip)] p-4 rounded-md;
    @apply bg-neutral-low-medium text-neutral-high-pure;
    @apply text-micro lg:site:text-3xs min-w-[230px];
    position: absolute !important;
    overflow: hidden;
    white-space: pre;
    font-weight: normal;
  }

  &:hover,
  &.show-tooltip {
    &::before,
    &::after {
      @apply block;
    }
  }

  &[data-variant="white"] {
    &::after {
      background-color: $tooltip-bg-white;
      color: $tooltip-bg-default;
      border: 1px solid $tooltip-border;
      box-shadow: $tooltip-shadow;
      font-size: 13px;
      line-height: 1.5;
      padding: 12px 16px;
      min-width: 250px;
      max-width: 300px;
      white-space: normal;
      word-wrap: break-word;
      display: none;
    }

    &.show-tooltip::after {
      display: block;
    }

    &::before {
      border-color: transparent;
    }
  }
}

@mixin tooltip-position($position, $arrow-color: $tooltip-bg-default, $white-arrow-color: $tooltip-bg-white) {
  @if $position == 'top' {
    &::before {
      bottom: 100%;
      left: 50%;
      transform: translate(-50%, -0.5em);
      border-bottom-width: 0;
      border-top-color: $arrow-color;
    }
    &::after {
      bottom: calc(100% + 5px);
      left: 50%;
      transform: translate(-50%, -0.5em);
    }
    &[data-variant="white"]::before {
      border-top-color: $white-arrow-color;
    }
    &[data-variant="white"][data-final-position="top"]::after {
      bottom: calc(100% + 8px);
      transform: translateX(-50%);
    }
  }

  @else if $position == 'bottom' {
    &::before {
      top: 105%;
      left: 50%;
      transform: translate(-50%, 0.5em);
      border-top-width: 0;
      border-bottom-color: $arrow-color;
    }
    &::after {
      top: calc(105% + 5px);
      left: 50%;
      transform: translate(-50%, 0.5em);
    }
    &[data-variant="white"]::before {
      border-bottom-color: $white-arrow-color;
    }
    &[data-variant="white"][data-final-position="bottom"]::after {
      top: calc(100% + 8px);
      transform: translateX(-50%);
    }
  }

  @else if $position == 'left' {
    &::before {
      top: 50%;
      left: calc(-9px);
      transform: translate(-0.5em, -50%);
      border-right-width: 0;
      border-left-color: $arrow-color;
    }
    &::after {
      top: 50%;
      right: calc(100% + 9px);
      transform: translate(-0.5em, -50%);
    }
    &[data-variant="white"]::before {
      border-left-color: $white-arrow-color;
    }
    &[data-variant="white"][data-final-position="left"]::after {
      right: calc(100% + 8px);
      transform: translateY(-50%);
    }
  }

  @else if $position == 'right' {
    &::before {
      top: 50%;
      right: calc(-5px);
      transform: translate(0.5em, -50%);
      border-left-width: 0;
      border-right-color: $arrow-color;
    }
    &::after {
      top: 50%;
      left: calc(100% + 5px);
      transform: translate(0.5em, -50%);
    }
    &[data-variant="white"]::before {
      right: calc(-8px);
      border-right-color: $white-arrow-color;
    }
    &[data-variant="white"][data-final-position="right"]::after {
      left: calc(100% + 8px);
      transform: translateY(-50%);
    }
  }
}

[data-tooltip] {
  &:not([position]),
  &[position^="top"] {
    @include tooltip-position('top');
  }

  &[position^="bottom"] {
    @include tooltip-position('bottom');
  }

  &[position^="left"] {
    @include tooltip-position('left');
  }

  &[position^="right"] {
    @include tooltip-position('right');
  }

  &:hover {
    &:not([position]),
    &[position^="top"],
    &[position^="bottom"] {
      &::before,
      &::after {
        animation: tooltips-vert 100ms ease-out forwards;
      }
    }

    &[position^="left"],
    &[position^="right"] {
      &::before,
      &::after {
        animation: tooltips-horz 100ms ease-out forwards;
      }
    }
  }
}

.tooltip-content-teleported {
  font-size: 13px;
  line-height: 1.5;
  padding: 12px 16px;
  min-width: 250px;
  max-width: 300px;
  white-space: normal;
  word-wrap: break-word;
  font-weight: normal;
  pointer-events: none;
  z-index: 999999 !important;
  border-radius: 8px;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    z-index: 999999 !important;
  }

  &[data-variant="default"] {
    background-color: $tooltip-bg-default;
    color: white;
    box-shadow: $tooltip-shadow;
  }

  &[data-variant="white"] {
    background-color: $tooltip-bg-white;
    color: $tooltip-bg-default;
    border: 1px solid $tooltip-border;
    box-shadow: $tooltip-shadow;
  }

  @each $position, $props in (
    'top': (top: 100%, left: 50%, transform: translateX(-50%), border-top: 6px solid),
    'bottom': (top: -6px, left: 50%, transform: translateX(-50%), border-bottom: 6px solid),
    'left': (right: -6px, top: 50%, transform: translateY(-50%), border-left: 6px solid),
    'right': (left: -6px, top: 50%, transform: translateY(-50%), border-right: 6px solid)
  ) {
    &[data-final-position="#{$position}"]::after {
      @each $prop, $value in $props {
        @if $prop == 'border-top' {
          #{$prop}: #{$value} var(--arrow-color);
        } @else if $prop == 'border-bottom' {
          #{$prop}: #{$value} var(--arrow-color);
        } @else if $prop == 'border-left' {
          #{$prop}: #{$value} var(--arrow-color);
        } @else if $prop == 'border-right' {
          #{$prop}: #{$value} var(--arrow-color);
        } @else {
          #{$prop}: #{$value};
        }
      }

      @if $position == 'left' or $position == 'right' {
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
      } @else {
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
      }
    }
  }

  &[data-variant="default"] {
    --arrow-color: #{$tooltip-bg-default};
  }

  &[data-variant="white"] {
    --arrow-color: #{$tooltip-bg-white};

    &[data-final-position]::after {
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
    }
  }
}

[tooltip=""]::before,
[tooltip=""]::after {
  display: none !important;
}

.tooltip-teleported[data-tooltip]::before,
.tooltip-teleported[data-tooltip]::after {
  display: none !important;
}

@keyframes tooltips-vert {
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes tooltips-horz {
  to {
    opacity: 1;
    transform: translate(0, -50%);
  }
}
</style>
